//src/components/FormSettings/RichTextEditorBase.jsx
//
// Enterprise shared rich-text editor foundation.
//
// PURPOSE
// - Centralize contentEditable behavior for all form-builder rich text surfaces.
// - Keep selection/caret stable while typing.
// - Prevent React from rewriting innerHTML while the editor is focused.
// - Preserve selection before toolbar controls steal focus.
// - Normalize Enter/paste behavior so line-height and spacing stay predictable.
// - Stay UI/context agnostic: no FieldEditContext, no FormSubmissionSettingsContext,
//   no Firestore writes, no HeaderSettings-specific commits.
//
// IMPORTANT
// This component is the shared editor foundation used by TextEditor.jsx
// and can later be reused by RichTextEditor2.jsx.
// - Parent components decide when to persist changes. This base only emits
//   onChange/onCommit payloads.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box } from '@mui/material';
import TextEditorToolbar from './TextEditorToolbar';

// Utility functions to map font sizes between px and HTML scale.
// Kept compatible with your current TextEditor/RichTextEditor behavior.
const pxToHtmlFontSize = {
  '12px': 1,
  '16px': 2,
  '24px': 3,
  '32px': 4,
  '48px': 5,
  '64px': 6,
  '96px': 7,
};

const htmlFontSizeToPx = {
  1: '12px',
  2: '16px',
  3: '24px',
  4: '32px',
  5: '48px',
  6: '64px',
  7: '96px',
};

const DEFAULT_EDITOR_STYLES = {
  bold: false,
  italic: false,
  underline: false,
  textAlign: 'left',
  fontSize: '16px',
  fontFamily: 'Arial',
  fontColor: '#000000',
  fontWeight: 'normal',
  fontStyle: 'normal',
  textDecoration: 'none',
  lineHeight: 1.25,
};

// Used only inside the live editor DOM to make caret placement after <br>
// reliable. It is stripped from emitted html/plainText payloads.
const ZERO_WIDTH_SPACE = '\u200B';

/**
 * Explicit editor change-source contract.
 *
 * WHY THIS EXISTS
 * Parent settings panels must be able to distinguish:
 * - ordinary typing / paste / blur commits
 * - toolbar formatting applied to selected text
 * - toolbar changes that intentionally affect root/base editor styles
 *
 * Without this explicit intent metadata, HeaderSettings cannot safely know
 * whether a generic editor style payload should overwrite persisted field
 * metadata such as subheaderStyle.subheaderColor.
 */
const EDITOR_CHANGE_SOURCE = {
  INPUT: 'input',
  TOOLBAR: 'toolbar',
  PASTE: 'paste',
  COMMIT: 'commit',
  UNKNOWN: 'unknown',
};

/**
 * Normalize incoming style objects from different legacy callers.
 *
 * Existing HeaderSettings passes:
 * - headerStyle.headerColor
 * - headerStyle.fontWeight
 * - subheaderStyle.subheaderColor
 * - subheaderStyle.subheaderFontWeight
 *
 * Existing TextEditor/RichTextEditor code expects:
 * - fontColor
 * - bold
 * - italic
 * - underline
 *
 * This adapter lets the shared base support both without forcing a migration
 * of all metadata schemas in Step 1.
 */
const normalizeIncomingStyles = (styles = {}) => {
  const fontColor =
    styles.fontColor ||
    styles.textColor ||
    styles.headerColor ||
    styles.subheaderColor ||
    DEFAULT_EDITOR_STYLES.fontColor;

  const fontSize =
    styles.fontSize ||
    styles.subFontSize ||
    DEFAULT_EDITOR_STYLES.fontSize;

  const fontWeight =
    styles.fontWeight ||
    styles.subheaderFontWeight ||
    (styles.bold ? 'bold' : DEFAULT_EDITOR_STYLES.fontWeight);

  const fontStyle =
    styles.fontStyle ||
    styles.subheaderFontStyle ||
    (styles.italic ? 'italic' : DEFAULT_EDITOR_STYLES.fontStyle);

  const textDecoration =
    styles.textDecoration ||
    styles.subheaderTextDecoration ||
    (styles.underline ? 'underline' : DEFAULT_EDITOR_STYLES.textDecoration);

  return {
    bold:
      styles.bold !== undefined
        ? Boolean(styles.bold)
        : fontWeight === 'bold' || Number(fontWeight) >= 700,

    italic:
      styles.italic !== undefined
        ? Boolean(styles.italic)
        : fontStyle === 'italic',

    underline:
      styles.underline !== undefined
        ? Boolean(styles.underline)
        : String(textDecoration || '').includes('underline'),

    textAlign: styles.textAlign || DEFAULT_EDITOR_STYLES.textAlign,
    fontSize,
    fontFamily:
      styles.fontFamily ||
      styles.headerFontFamily ||
      styles.subheaderFontFamily ||
      DEFAULT_EDITOR_STYLES.fontFamily,
    fontColor,
    fontWeight,
    fontStyle,
    textDecoration,
    lineHeight: styles.lineHeight || DEFAULT_EDITOR_STYLES.lineHeight,
  };
};

/**
 * Build outbound styles.
 *
 * This keeps the editor engine generic while still returning compatibility keys
 * that your current HeaderSettings/HeaderFieldComponent flow can consume later.
 */
const buildOutboundStyles = (styles = {}) => {
  const fontWeight = styles.bold ? 'bold' : 'normal';
  const fontStyle = styles.italic ? 'italic' : 'normal';
  const textDecoration = styles.underline ? 'underline' : 'none';

  return {
    ...styles,

    // Editor-native style shape
    fontWeight,
    fontStyle,
    textDecoration,
    textColor: styles.fontColor,

    // Header renderer compatibility
    headerColor: styles.fontColor,
    subheaderColor: styles.fontColor,
    subFontSize: styles.fontSize,
    subheaderFontWeight: fontWeight,
    subheaderFontStyle: fontStyle,
    subheaderTextDecoration: textDecoration,
  };
};

const shallowEqual = (obj1, obj2) => {
  if (obj1 === obj2) return true;
  if (!obj1 || !obj2) return false;

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  return keys1.every((key) => obj1[key] === obj2[key]);
};

const normalizeColorToHex = (color) => {
  if (!color) return DEFAULT_EDITOR_STYLES.fontColor;

  if (typeof color === 'string' && color.startsWith('#')) {
    if (color.length === 4) {
      const r = color[1];
      const g = color[2];
      const b = color[3];

      return `#${r}${r}${g}${g}${b}${b}`;
    }

    return color.slice(0, 7);
  }

  const rgbMatch = String(color).match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
  if (!rgbMatch) return DEFAULT_EDITOR_STYLES.fontColor;

  const [, r, g, b] = rgbMatch;

  return `#${[r, g, b]
    .map((value) => Number(value).toString(16).padStart(2, '0'))
    .join('')}`;
};

const escapeHtml = (text = '') =>
  String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const getEditorText = (editor) => {
  if (!editor) return '';

  return (editor.innerText || editor.textContent || '').replaceAll(ZERO_WIDTH_SPACE, '');
};

const getEditorHtml = (editor) => {
  if (!editor) return '';

  return (editor.innerHTML || '').replaceAll(ZERO_WIDTH_SPACE, '');
};

const RichTextEditorBase = ({
  value = '',
  styles = {},
  placeholder = '',
  onChange,
  onCommit,

  // Toolbar configuration
  ToolbarComponent = TextEditorToolbar,
  toolbarProps = {},
  toolbarSx,

  // Editor behavior
  commitOnBlur = true,
  preservePastedFormatting = false,
  normalizeLineBreaks = true,
  disabled = false,
  readOnly = false,

  // Styling
  minHeight = 100,
  sx,
  editorSx,

  // Optional extension points for future specialized wrappers
  normalizeStyles = normalizeIncomingStyles,
  serializeStyles = buildOutboundStyles,
  onFocus,
  onBlur,
}) => {
  const editorRef = useRef(null);
  const savedSelectionRef = useRef(null);
  const isFocusedRef = useRef(false);
  const hasHydratedRef = useRef(false);
  const isComposingRef = useRef(false);
  const lastKnownHtmlRef = useRef(value || '');

const normalizedInitialStyles = useMemo(
  () => normalizeStyles(styles),
  [normalizeStyles, styles]
);

// baseStyles = the whole editor/header default style.
// activeStyles = toolbar state for the current selection/caret.
// Do not use activeStyles to style the entire editor root, or selected text
// formatting will accidentally become whole-field formatting.
const [baseStyles, setBaseStyles] = useState(normalizedInitialStyles);
const [activeStyles, setActiveStyles] = useState(normalizedInitialStyles);

const [isEmpty, setIsEmpty] = useState(() => {
  const initialValue = value || '';

  return initialValue.length === 0;
});

  const editorContainsNode = useCallback((node) => {
    if (!editorRef.current || !node) return false;

    return (
      editorRef.current === node ||
      editorRef.current.contains(node)
    );
  }, []);

  const saveSelection = useCallback(() => {
    if (typeof window === 'undefined') return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const container = range.commonAncestorContainer;

    if (!editorContainsNode(container)) return;

    savedSelectionRef.current = range.cloneRange();
  }, [editorContainsNode]);

  const restoreSelection = useCallback(() => {
    if (typeof window === 'undefined') return false;
    if (!editorRef.current || !savedSelectionRef.current) return false;

    const selection = window.getSelection();
    if (!selection) return false;

    try {
      editorRef.current.focus();

      selection.removeAllRanges();
      selection.addRange(savedSelectionRef.current);

      return true;
    } catch (error) {
      console.warn('[RichTextEditorBase:restoreSelection] Failed to restore selection:', error);
      return false;
    }
  }, []);

  const buildPayload = useCallback(
    (nextBaseStyles = baseStyles, changeIntent = {}) => {
      const editor = editorRef.current;
      const html = getEditorHtml(editor);
      const text = getEditorText(editor);
      const serializedStyles = serializeStyles(nextBaseStyles);

      return {
        // Keep legacy TextEditor compatibility: text is still html.
        text: html,
        html,
        plainText: text,
        styles: serializedStyles,
        isEmpty: text.trim().length === 0,

        // Enterprise editor intent metadata.
        // Parent settings panels use this to decide whether an emitted style
        // should update persisted metadata or only remain inline editor HTML.
        changeSource: changeIntent.changeSource || EDITOR_CHANGE_SOURCE.UNKNOWN,
        changedStyleKey: changeIntent.changedStyleKey || null,
        changedStyleValue:
          changeIntent.changedStyleValue !== undefined
            ? changeIntent.changedStyleValue
            : null,
        affectsRootStyle: Boolean(changeIntent.affectsRootStyle),
      };
    },
    [baseStyles, serializeStyles]
  );

  const emitChange = useCallback((nextBaseStyles = baseStyles, changeIntent = {}) => {
    if (!editorRef.current) return;

    const payload = buildPayload(nextBaseStyles, changeIntent);

    lastKnownHtmlRef.current = payload.html;
    setIsEmpty(payload.isEmpty);

    if (onChange) {
      onChange(payload);
    }
  }, [baseStyles, buildPayload, onChange]);

  const emitCommit = useCallback((nextBaseStyles = baseStyles, changeIntent = {}) => {
    if (!editorRef.current) return;

    const payload = buildPayload(nextBaseStyles, {
      changeSource: EDITOR_CHANGE_SOURCE.COMMIT,
      changedStyleKey: null,
      changedStyleValue: null,
      affectsRootStyle: false,
      ...changeIntent,
    });

    lastKnownHtmlRef.current = payload.html;
    setIsEmpty(payload.isEmpty);

    if (onCommit) {
      onCommit(payload);
    }
  }, [baseStyles, buildPayload, onCommit]);

  // Hydrate editor content and styles from props.
  //
  // ENTERPRISE RULE:
  // Never rewrite contentEditable.innerHTML while focused/typing.
  // The browser owns the active selection range, and replacing innerHTML is the
  // source of cursor jumps, selection loss, and unexpected spacing changes.
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const incomingStyles = normalizeStyles(styles);
    const shouldSyncStyles = !isFocusedRef.current || !hasHydratedRef.current;

    if (shouldSyncStyles) {
  setBaseStyles((prev) =>
    shallowEqual(prev, incomingStyles) ? prev : incomingStyles
  );

  setActiveStyles((prev) =>
    shallowEqual(prev, incomingStyles) ? prev : incomingStyles
  );
}

    const incomingHtml = value || '';
    const currentHtml = editor.innerHTML;

    const shouldHydrateHtml =
      !hasHydratedRef.current ||
      (!isFocusedRef.current &&
        incomingHtml !== currentHtml &&
        incomingHtml !== lastKnownHtmlRef.current);

    if (shouldHydrateHtml) {
      editor.innerHTML = incomingHtml;
      lastKnownHtmlRef.current = incomingHtml;
      hasHydratedRef.current = true;
      setIsEmpty(getEditorText(editor).trim().length === 0);
    }

//    console.log('[RichTextEditorBase:useEffect] Sync check:', {
//      isFocused: isFocusedRef.current,
//      shouldSyncStyles,
//      shouldHydrateHtml,
//      incomingHtml,
//      currentHtml,
//      incomingStyles,
//    });
  }, [normalizeStyles, styles, value]);

  const updateToolbarState = useCallback(() => {
    if (typeof window === 'undefined') return;

    const selection = window.getSelection();

    if (!selection || !selection.rangeCount) {
      return;
    }

    const range = selection.getRangeAt(0);
    let node = range.startContainer;

    if (node.nodeType === Node.TEXT_NODE) {
      node = node.parentNode;
    }

    if (editorRef.current && editorRef.current.contains(node)) {
      const computedStyle = window.getComputedStyle(node);

      const computedFontSize = computedStyle.fontSize || activeStyles.fontSize;
      const normalizedFontSize =
        Object.values(htmlFontSizeToPx).includes(computedFontSize)
          ? computedFontSize
          : activeStyles.fontSize;

      const updatedStyles = {
        ...activeStyles,
        bold:
          computedStyle.fontWeight === 'bold' ||
          parseInt(computedStyle.fontWeight, 10) >= 700,
        italic: computedStyle.fontStyle === 'italic',
        underline: computedStyle.textDecoration.includes('underline'),
        textAlign: computedStyle.textAlign || activeStyles.textAlign,
        fontSize: normalizedFontSize,
        fontFamily:
          computedStyle.fontFamily?.split(',')[0].replace(/['"]/g, '') ||
          activeStyles.fontFamily,
        fontColor: normalizeColorToHex(computedStyle.color || activeStyles.fontColor),
        fontWeight: computedStyle.fontWeight || activeStyles.fontWeight,
        fontStyle: computedStyle.fontStyle || activeStyles.fontStyle,
        textDecoration: computedStyle.textDecoration || activeStyles.textDecoration,
      };

      setActiveStyles((prev) =>
        shallowEqual(prev, updatedStyles) ? prev : updatedStyles
      );

      saveSelection();
//      console.log('[RichTextEditorBase:updateToolbarState] Updated toolbar activeStyles:', updatedStyles);
    }
  }, [activeStyles, saveSelection]);

  const handleStyleChange = useCallback(
    (key, nextValue) => {
//      console.log(`[RichTextEditorBase:handleStyleChange] Key: ${key}, Value: ${nextValue}`);

      if (!editorRef.current || disabled || readOnly) return;

      restoreSelection();

      try {
        document.execCommand('styleWithCSS', false, true);
      } catch (error) {
        // Non-fatal. Some browsers may ignore styleWithCSS.
      }

      if (key === 'textAlign') {
        document.execCommand(
          'justify' +
            String(nextValue).charAt(0).toUpperCase() +
            String(nextValue).slice(1)
        );

        const updatedStyles = {
          ...activeStyles,
          textAlign: nextValue,
        };

        const nextBaseStyles = {
          ...baseStyles,
          textAlign: nextValue,
        };

        setActiveStyles(updatedStyles);
        setBaseStyles(nextBaseStyles);
        saveSelection();

        // Alignment is intentionally treated as a root/base editor style.
        // HeaderSettings may persist this into headerStyle/subheaderStyle.
        emitChange(nextBaseStyles, {
          changeSource: EDITOR_CHANGE_SOURCE.TOOLBAR,
          changedStyleKey: key,
          changedStyleValue: nextValue,
          affectsRootStyle: true,
        });
        return;
      }
      switch (key) {
        case 'bold':
        case 'italic':
        case 'underline':
          document.execCommand(key, false, null);
          break;

        case 'fontSize': {
          const htmlSize = pxToHtmlFontSize[nextValue] || 3;
          document.execCommand('fontSize', false, htmlSize);

          const fontElements = editorRef.current.getElementsByTagName('font');
          Array.from(fontElements).forEach((font) => {
            if (font.size === `${htmlSize}`) {
              font.removeAttribute('size');
              font.style.fontSize = nextValue;
            }
          });
          break;
        }

        case 'fontFamily':
          document.execCommand('fontName', false, nextValue);
          break;

        case 'fontColor':
          document.execCommand('foreColor', false, nextValue);
          break;

        default:
          break;
      }

      const updatedStyles = {
        ...activeStyles,
        [key]: nextValue,
        ...(key === 'bold' ? { fontWeight: nextValue ? 'bold' : 'normal' } : {}),
        ...(key === 'italic' ? { fontStyle: nextValue ? 'italic' : 'normal' } : {}),
        ...(key === 'underline' ? { textDecoration: nextValue ? 'underline' : 'none' } : {}),
      };

      setActiveStyles(updatedStyles);
      saveSelection();

//      console.log('[RichTextEditorBase:handleStyleChange] Updated activeStyles:', updatedStyles);

      // Selection/caret formatting is reflected in the editor HTML.
      // Do not emit activeStyles as metadata/root styles.
      // The intent metadata still tells parent settings panels that the user
      // intentionally used the toolbar, but affectsRootStyle=false means the
      // persisted root metadata should not be overwritten by this style payload.
      emitChange(baseStyles, {
        changeSource: EDITOR_CHANGE_SOURCE.TOOLBAR,
        changedStyleKey: key,
        changedStyleValue: nextValue,
        affectsRootStyle: false,
      });
    },
	[
	  activeStyles,
	  baseStyles,
	  disabled,
	  emitChange,
	  readOnly,
	  restoreSelection,
	  saveSelection,
	]
  );

  const handleInput = useCallback(() => {
    if (disabled || readOnly) return;
    if (isComposingRef.current) return;

    saveSelection();

    emitChange(baseStyles, {
      changeSource: EDITOR_CHANGE_SOURCE.INPUT,
      changedStyleKey: null,
      changedStyleValue: null,
      affectsRootStyle: false,
    });
  }, [baseStyles, disabled, emitChange, readOnly, saveSelection]);

  const handleFocus = useCallback(
    (event) => {
      isFocusedRef.current = true;
      saveSelection();

      if (onFocus) {
        onFocus(event);
      }
    },
    [onFocus, saveSelection]
  );

const handleBlur = useCallback(
  (event) => {
    isFocusedRef.current = false;
    saveSelection();

    if (commitOnBlur) {
      emitCommit(baseStyles, {
        changeSource: EDITOR_CHANGE_SOURCE.COMMIT,
        changedStyleKey: null,
        changedStyleValue: null,
        affectsRootStyle: false,
      });
    }

    if (onBlur) {
      onBlur(event);
    }
  },
  [baseStyles, commitOnBlur, emitCommit, onBlur, saveSelection]
);

const insertManagedLineBreak = useCallback(() => {
  if (typeof window === 'undefined') return false;

  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return false;

  const range = selection.getRangeAt(0);

  if (!editorContainsNode(range.commonAncestorContainer)) {
    return false;
  }

  range.deleteContents();

  const br = document.createElement('br');
  const marker = document.createTextNode(ZERO_WIDTH_SPACE);

  range.insertNode(br);
  range.setStartAfter(br);
  range.collapse(true);

  range.insertNode(marker);
  range.setStartAfter(marker);
  range.collapse(true);

  selection.removeAllRanges();
  selection.addRange(range);

  return true;
}, [editorContainsNode]);

const handleKeyDown = useCallback(
  (event) => {
    if (disabled || readOnly) return;
    if (!normalizeLineBreaks) return;
    if (event.key !== 'Enter') return;

    // Keep line behavior predictable.
    // Native contentEditable may generate <div>, <p>, or extra margins
    // depending on browser/cursor location. Insert a managed <br> + caret marker.
    event.preventDefault();

    restoreSelection();

    const inserted = insertManagedLineBreak();

    if (!inserted) {
      console.warn('[RichTextEditorBase:handleKeyDown] Failed to insert managed line break.');
      return;
    }

    requestAnimationFrame(() => {
      saveSelection();
      emitChange(baseStyles, {
        changeSource: EDITOR_CHANGE_SOURCE.INPUT,
        changedStyleKey: null,
        changedStyleValue: null,
        affectsRootStyle: false,
      });
    });
  },
  [
    baseStyles,
    disabled,
    emitChange,
    insertManagedLineBreak,
    normalizeLineBreaks,
    readOnly,
    restoreSelection,
    saveSelection,
  ]
);

  const handlePaste = useCallback(
    (event) => {
      if (disabled || readOnly) return;
      if (preservePastedFormatting) return;

      // Default enterprise behavior for form-builder text surfaces:
      // paste plain text to prevent arbitrary <p>, <div>, margins, scripts,
      // or external inline styles from breaking spacing.
      event.preventDefault();

      const clipboardText =
        event.clipboardData?.getData('text/plain') ||
        window.clipboardData?.getData('Text') ||
        '';

      const safeHtml = escapeHtml(clipboardText).replace(/\r?\n/g, '<br>');

      restoreSelection();

      try {
        document.execCommand('insertHTML', false, safeHtml);
      } catch (error) {
        console.warn('[RichTextEditorBase:handlePaste] Failed to insert pasted text:', error);
      }

      requestAnimationFrame(() => {
        saveSelection();
        emitChange(baseStyles, {
          changeSource: EDITOR_CHANGE_SOURCE.PASTE,
          changedStyleKey: null,
          changedStyleValue: null,
          affectsRootStyle: false,
        });
      });
    },
[
  baseStyles,
  disabled,
  emitChange,
  preservePastedFormatting,
  readOnly,
  restoreSelection,
  saveSelection,
]
  );

  const contentEditableValue = disabled || readOnly ? false : true;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        ...sx,
      }}
    >
      {ToolbarComponent && (
        <ToolbarComponent
          activeStyles={activeStyles}
          onStyleChange={handleStyleChange}
          onToolbarMouseDown={saveSelection}
          sx={toolbarSx}
          disabled={disabled || readOnly}
          {...toolbarProps}
        />
      )}

      <Box
        sx={{
          position: 'relative',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          minHeight,
          p: 2,
          bgcolor: disabled ? 'action.disabledBackground' : 'background.paper',
          cursor: disabled || readOnly ? 'default' : 'text',
          ...editorSx,
        }}
      >
        {isEmpty && placeholder && (
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              right: 16,
              color: 'text.disabled',
              opacity: 0.75,
              pointerEvents: 'none',
              fontStyle: 'italic',
			fontSize: baseStyles.fontSize,
			fontFamily: baseStyles.fontFamily,
			textAlign: baseStyles.textAlign,
			lineHeight: baseStyles.lineHeight || 1.25,
            }}
          >
            {placeholder}
          </Box>
        )}

        <Box
          ref={editorRef}
          contentEditable={contentEditableValue}
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-disabled={disabled || readOnly ? 'true' : undefined}
          onInput={handleInput}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onMouseUp={updateToolbarState}
          onKeyUp={updateToolbarState}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onCompositionStart={() => {
            isComposingRef.current = true;
          }}
          onCompositionEnd={() => {
            isComposingRef.current = false;
            handleInput();
          }}
          sx={{
            outline: 'none',
            minHeight: Math.max(Number(minHeight) - 32 || 0, 40),

            // Stable editor layout styles
            whiteSpace: 'pre-wrap',
            overflowWrap: 'break-word',
            wordBreak: 'break-word',
			// Root/default styles come from baseStyles only.
			// Selection-specific toolbar state must not change the whole editor.
			textAlign: baseStyles.textAlign,
			fontSize: baseStyles.fontSize,
			fontFamily: baseStyles.fontFamily,
			color: baseStyles.fontColor,
			fontWeight: baseStyles.bold ? 'bold' : baseStyles.fontWeight,
			fontStyle: baseStyles.italic ? 'italic' : baseStyles.fontStyle,
			textDecoration: baseStyles.underline
			  ? 'underline'
			  : baseStyles.textDecoration,
			lineHeight: baseStyles.lineHeight || 1.25,

            // Prevent browser-generated block children from creating large line gaps.
            '& p, & div': {
              margin: 0,
              padding: 0,
              lineHeight: 'inherit',
            },

            '& font': {
              lineHeight: 'inherit',
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default RichTextEditorBase;